import { useState, useEffect, useCallback } from 'react';
import { Table, TableHeader, TableBody, Column, Row } from 'react-aria-components';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { useAtom } from 'jotai';

import { createUser } from '@/api/createUser';
import { deleteUser } from '@/api/deleteUser';
import { getOffices } from '@/api/getOffices';
import { getUsers } from '@/api/getUsers';
import { updateUser } from '@/api/updateUser';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import CreateUserForm from '@/components/CreateUserForm/CreateUserForm';
import EditUserForm from '@/components/EditUserForm/EditUserForm';
import InputText from '@/components/InputText/InputText';
import { Pagination } from '@/components/Pagination/Pagination';
import { NotificationAtom, UserAtom } from '@/jotai/jotai';
import { Office } from '@/types/office';
import { User, UserRequest } from '@/types/user';

const defaultLimit = 15;

type SearchInput = {
  word: string;
};

export const AccountPage = () => {
  const email = localStorage.getItem('email') || '';
  const { register, handleSubmit } = useForm<SearchInput>();
  const [users, setUsers] = useState<User[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [user, setUser] = useState<User>();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [_, setNotification] = useAtom(NotificationAtom);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loginUser] = useAtom(UserAtom);

  const { pathname: pathName } = useLocation();
  const breadcrumbs: BreadcrumbItem[] = [{ path: pathName, name: 'アカウント一覧' }];

  useEffect(() => {
    const fetchUsers = async () => {
      const result = await getUsers(
        defaultLimit.toString(),
        ((currentPage - 1) * defaultLimit).toString(),
        undefined,
        loginUser?.office_ids ?? []
      );
      if (result.status === 200) {
        setUsers(result.result.users);
        setTotalPages(
          result.result.total === 0 ? 1 : Math.ceil(result.result.total / defaultLimit)
        );
      }
    };
    fetchUsers();
    fetchOffices();
  }, [currentPage]);

  const fetchOffices = useCallback(async () => {
    try {
      const result = await getOffices(email);
      if (result.status === 200) {
        setOffices(result.result);
      } else {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: 'ユーザー情報の取得に失敗しました',
          message: 'ユーザー情報の取得に失敗しました',
        });
      }
    } catch (error) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: 'ユーザー取得に失敗しました',
        message: 'ユーザー取得に失敗しました',
      });
    }
  }, []);

  const onSubmit: SubmitHandler<SearchInput> = async (data) => {
    try {
      const result = await getUsers(
        defaultLimit.toString(),
        ((currentPage - 1) * defaultLimit).toString(),
        data.word,
        loginUser?.office_ids ?? []
      );
      if (result.status === 200) {
        setUsers(result.result.users);
        setTotalPages(
          result.result.total === 0 ? 1 : Math.ceil(result.result.total / defaultLimit)
        );
      } else {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: 'ユーザー取得に失敗しました',
          message: 'ユーザー取得に失敗しました',
        });
      }
    } catch (error) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: 'ユーザー取得に失敗しました',
        message: 'ユーザー取得に失敗しました',
      });
    }
  };

  const onClickUserEdit = (u: User) => {
    setUser(u);
    setIsOpenEdit(true);
  };

  const onSubmitCreate: SubmitHandler<UserRequest> = async (user) => {
    try {
      const result = await createUser(user);
      if (result.status === 201) {
        setNotification({
          headingLevel: 'h2',
          type: 'success',
          bannerStyle: 'standard',
          title: 'ユーザー登録が完了しました',
          message: 'ユーザー登録が完了しました',
        });
        setIsOpen(false);
      }
    } catch (e) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: 'ユーザー登録に失敗しました',
        message: 'ユーザー登録に失敗しました',
      });
    }
  };

  const onSubmitUpdate: SubmitHandler<UserRequest> = async (data) => {
    try {
      const request: UserRequest = {
        last_name: data.last_name,
        first_name: data.first_name,
        last_name_kana: data.last_name_kana,
        first_name_kana: data.first_name_kana,
        email: data.email,
        phone_number: data.phone_number,
        line_invite_link: data.line_invite_link,
        role: data.role,
        office_ids: data.office_ids,
        children: data.children
          ? data.children
              .filter((c) => c.last_name && c.first_name)
              .map((c) => ({
                last_name: c.last_name,
                first_name: c.first_name,
                office_ids: c.office_ids,
              }))
          : undefined,
      };
      const result = await updateUser(request);
      if (result.status === 200) {
        setNotification({
          headingLevel: 'h2',
          type: 'success',
          bannerStyle: 'standard',
          title: 'ユーザー情報を更新しました',
          message: 'ユーザー情報を更新しました',
        });
        setIsOpenEdit(false);
      }
    } catch (e) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: 'ユーザー情報の更新に失敗しました',
        message: 'ユーザー情報の更新に失敗しました',
      });
    }
  };

  const onSubmitDelete = async (user: User) => {
    const result = await deleteUser(user.role, user.email);
    if (result.status === 204) {
      setNotification({
        headingLevel: 'h2',
        type: 'success',
        bannerStyle: 'standard',
        title: 'ユーザー削除が完了しました',
        message: 'ユーザー削除が完了しました',
      });
      setIsOpenEdit(false);
    } else {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: 'ユーザー削除に失敗しました',
        message: 'ユーザー削除に失敗しました',
      });
    }
  };

  const handlePageChange = async (newPage: number) => {
    setCurrentPage(newPage);

    const offset = (newPage - 1) * defaultLimit;
    try {
      const result = await getUsers(
        defaultLimit.toString(),
        offset.toString(),
        undefined,
        loginUser?.office_ids ?? []
      );
      if (result.status === 200) {
        setUsers(result.result.users);
      } else {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: 'ユーザー情報の取得に失敗しました',
          message: 'ユーザー情報の取得に失敗しました',
        });
      }
    } catch (e) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: 'ユーザー情報の取得中にエラーが発生しました',
        message: 'ユーザー情報の取得中にエラーが発生しました',
      });
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <div className="mb-4 flex items-center justify-between bg-[#F2F2F266] px-5">
        <div className="flex flex-row items-center space-x-2">
          <InputText
            name="word"
            type="word"
            register={register('word')}
          />
          <Button
            size="lg"
            variant="solid-fill"
            type="submit"
            onClick={handleSubmit(onSubmit)}
          >
            検索
          </Button>
        </div>

        <div className="flex-shrink-0">
          <Button
            size="lg"
            variant="solid-fill"
            type="submit"
            onClick={() => setIsOpen(true)}
          >
            新規登録
          </Button>
          <CreateUserForm
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            onSubmit={onSubmitCreate}
            offices={offices}
          />
        </div>
      </div>

      <Table className="w-full border-collapse border border-gray-200">
        <TableHeader>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            アカウント名(姓)
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            アカウント名(名)
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            連絡先TEL
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            メールアドレス
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            アカウント権限
          </Column>
          {/* <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            児童名(姓)
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            児童名(名)
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            LIN招待URL
          </Column> */}
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <Row key={user.role + '-' + user.email}>
              <Column className="border border-gray-200 px-4 py-2">{user.last_name}</Column>
              <Column className="border border-gray-200 px-4 py-2">{user.first_name}</Column>
              <Column className="border border-gray-200 px-4 py-2">{user.phone_number}</Column>
              <Column className="border border-gray-200 px-4 py-2">{user.email}</Column>
              <Column className="border border-gray-200 px-4 py-2">{user.role}</Column>
              {/* <Column className="border border-gray-200 px-4 py-2">{user.child_last_name}</Column>
              <Column className="border border-gray-200 px-4 py-2">{user.child_first_name}</Column>
              <Column className="border border-gray-200 px-4 py-2">
                <a
                  href={user.line_invite_link}
                  target="_blank"
                  rel="noreferrer"s
                >
                  {user.line_invite_link}
                </a>
              </Column> */}
              <Column className="border border-gray-200 px-4 py-2">
                <Button
                  size="sm"
                  variant="text"
                  type="submit"
                  onClick={() => onClickUserEdit(user)}
                >
                  編集
                </Button>
              </Column>
            </Row>
          ))}
        </TableBody>
      </Table>

      <EditUserForm
        isOpen={isOpenEdit}
        setIsOpen={setIsOpenEdit}
        user={user}
        onSubmit={onSubmitUpdate}
        onDelete={onSubmitDelete}
        offices={offices}
      />

      <div className="flex items-center justify-center gap-4 py-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
